import { useState, useMemo, useCallback } from 'react';
import type { Workflow, SortColumn, SortDirection } from '@/types/workflow';

interface _WorkflowFilters {
  statusFilter: number[];
  workflowFilter: string;
  sortBy: SortColumn;
  sortDirection: SortDirection;
}

export const useWorkflowFilters = (workflows: Workflow[]) => {
  // State for filters
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [workflowFilter, setWorkflowFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortColumn>('startTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Combined filter function
  const filteredWorkflows = useMemo(() => {
    return workflows.filter((workflow) => {
      // Status filter: if empty array, show all; otherwise show only selected statuses
      const statusMatch = statusFilter.length === 0 || statusFilter.includes(workflow.status);

      // Workflow filter: if empty, show all; otherwise match name, workflow ID, or run ID (partial match)
      const workflowMatch =
        !workflowFilter ||
        workflow.name.toLowerCase().includes(workflowFilter.toLowerCase()) ||
        workflow.id.toLowerCase().includes(workflowFilter.toLowerCase()) ||
        workflow.runId.toLowerCase().includes(workflowFilter.toLowerCase());

      return statusMatch && workflowMatch;
    });
  }, [workflows, statusFilter, workflowFilter]);

  // Sort function with proper data type handling
  const sortedAndFilteredWorkflows = useMemo(() => {
    return [...filteredWorkflows].sort((a: Workflow, b: Workflow) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'startTime':
          aValue = new Date(a.startTime).getTime();
          bValue = new Date(b.startTime).getTime();
          break;
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'duration': {
          // Calculate duration in milliseconds
          const aEnd = a.endTime ? new Date(a.endTime).getTime() : Date.now();
          const bEnd = b.endTime ? new Date(b.endTime).getTime() : Date.now();
          aValue = aEnd - new Date(a.startTime).getTime();
          bValue = bEnd - new Date(b.startTime).getTime();
          break;
        }
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredWorkflows, sortBy, sortDirection]);

  // Sort handler
  const handleSort = useCallback(
    (column: SortColumn) => {
      if (sortBy === column) {
        // Toggle direction if same column
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        // New column, default to ascending
        setSortBy(column);
        setSortDirection('asc');
      }
    },
    [sortBy]
  );

  // Clear filters
  const clearFilters = useCallback(() => {
    setStatusFilter([]);
    setWorkflowFilter('');
    setSortBy('startTime');
    setSortDirection('desc');
  }, []);

  return {
    // State
    statusFilter,
    workflowFilter,
    sortBy,
    sortDirection,

    // State setters
    setStatusFilter,
    setWorkflowFilter,

    // Computed data
    filteredAndSortedWorkflows: sortedAndFilteredWorkflows,

    // Actions
    handleSort,
    clearFilters,

    // Stats
    totalCount: workflows.length,
    filteredCount: sortedAndFilteredWorkflows.length,
  };
};
